import React from 'react';
import { Box, Paper, Tooltip, makeStyles } from '@material-ui/core';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import TableChartIcon from '@material-ui/icons/TableChart';
import PresentToAllIcon from '@material-ui/icons/PresentToAll';
import AttachmentIcon from '@material-ui/icons/Attachment';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import ImageIcon from '@material-ui/icons/Image';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';

const useStyles = makeStyles((theme) => ({
  documentBubble: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: '100px',
    maxWidth: '120px',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: '#eeeeee',
      boxShadow: theme.shadows[2],
      transform: 'translateY(-2px)',
    },
  },
  bubbleWrapper: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 0,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: '40px',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  fileName: {
    fontSize: '0.75rem',
    fontWeight: 500,
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    maxHeight: '2.5em',
  },
}));

interface DocumentBubbleProps {
  fileName: string;
  mimeType?: string;
  onClick: () => void;
}

export const DocumentBubble: React.FC<DocumentBubbleProps> = ({
  fileName,
  mimeType = '',
  onClick,
}) => {
  const classes = useStyles();
  const normalizedMimeType = (mimeType || '').toLowerCase();
  const normalizedFileName = (fileName || '').toLowerCase();
  const fileExtension = (normalizedFileName.split('.').pop() || '').toLowerCase();
  const isCsvFile = normalizedMimeType.includes('csv') || fileExtension === 'csv';
  const isWordFile =
    normalizedMimeType.includes('word') ||
    normalizedMimeType.includes('document') ||
    normalizedMimeType.includes('wordprocessingml') ||
    ['doc', 'docx', 'rtf'].indexOf(fileExtension) > -1;
  const isSheetFile =
    normalizedMimeType.includes('sheet') ||
    normalizedMimeType.includes('excel') ||
    normalizedMimeType.includes('spreadsheetml') ||
    ['xls', 'xlsx', 'csv'].indexOf(fileExtension) > -1;
  const isPresentationFile =
    normalizedMimeType.includes('presentation') ||
    normalizedMimeType.includes('powerpoint') ||
    normalizedMimeType.includes('presentationml') ||
    ['ppt', 'pptx'].indexOf(fileExtension) > -1;
  const isImageFile = normalizedMimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].indexOf(fileExtension) > -1;
  const isAudioFile = normalizedMimeType.startsWith('audio/') || ['mp3', 'wav', 'm4a', 'amr', 'ogg'].indexOf(fileExtension) > -1;
  const isVideoFile = normalizedMimeType.startsWith('video/') || ['mp4', 'mov', 'mpeg', '3gp', 'webm'].indexOf(fileExtension) > -1;

  const getIconForMimeType = (): React.ReactElement => {
    if (normalizedMimeType.includes('pdf') || fileExtension === 'pdf') {
      return <PictureAsPdfIcon className={classes.icon} style={{ color: '#d32f2f' }} />;
    }
    if (isImageFile) {
      return <ImageIcon className={classes.icon} style={{ color: '#00838f' }} />;
    }
    if (isAudioFile) {
      return <MusicNoteIcon className={classes.icon} style={{ color: '#6a1b9a' }} />;
    }
    if (isVideoFile) {
      return <OndemandVideoIcon className={classes.icon} style={{ color: '#1565c0' }} />;
    }
    if (isWordFile) {
      return <DescriptionIcon className={classes.icon} style={{ color: '#1976d2' }} />;
    }
    if (isCsvFile) {
      return <TableChartIcon className={classes.icon} style={{ color: '#388e3c' }} />;
    }
    if (isSheetFile) {
      return <TableChartIcon className={classes.icon} style={{ color: '#388e3c' }} />;
    }
    if (isPresentationFile) {
      return <PresentToAllIcon className={classes.icon} style={{ color: '#f57c00' }} />;
    }
    if (normalizedMimeType.includes('text')) {
      return <TextFieldsIcon className={classes.icon} />;
    }
    return <AttachmentIcon className={classes.icon} />;
  };

  const getFileTypeLabel = (): string => {
    if (normalizedMimeType.includes('pdf') || fileExtension === 'pdf') return 'PDF';
    if (isWordFile) return 'Word';
    if (isCsvFile) return 'CSV';
    if (isSheetFile) return 'Sheet';
    if (isPresentationFile) return 'Presentation';
    if (isImageFile) return 'Image';
    if (isAudioFile) return 'Audio';
    if (isVideoFile) return 'Video';
    if (normalizedMimeType.includes('text') || ['txt', 'log', 'json', 'xml'].indexOf(fileExtension) > -1) return 'Text';
    return 'File';
  };

  return (
    <Box className={classes.bubbleWrapper}>
      <Tooltip
        title={`Click to preview - ${fileName}`}
        placement="top"
        arrow
      >
        <Paper
          className={classes.documentBubble}
          onClick={onClick}
          elevation={0}
        >
          {getIconForMimeType()}
          <Box className={classes.fileName} title={fileName}>
            {fileName}
          </Box>
        </Paper>
      </Tooltip>
    </Box>
  );
};

export default DocumentBubble;
